import { CATALOG, presetFor } from "../avatar/catalog";
import { avatarPartsReady, sheetDataURL } from "../avatar/composer";
import { ASSETS, AVATAR_FRAME } from "../config";
import type { PublicUser } from "../types";
import { GestureMenu } from "./GestureMenu";
import { VoicePanel } from "./VoicePanel";

/** Escala do avatar em miniatura no botao de perfil (frame de 16x24). */
const PROFILE_THUMB_SCALE = 1;

/** Limiares de latencia (ms) para o ponto colorido do ping. */
const LATENCY_GOOD_MAX = 100;
const LATENCY_WARN_MAX = 250;

/** Controla o HUD em DOM sobreposto ao canvas do Phaser. */
export class Hud {
  private readonly root = requireElement<HTMLDivElement>("hud");
  private readonly onlineEl = requireElement<HTMLSpanElement>("hud-online");
  private readonly latencyDotEl = requireElement<HTMLSpanElement>("hud-latency-dot");
  private readonly latencyValueEl = requireElement<HTMLSpanElement>("hud-latency-value");
  private readonly hintEl = requireElement<HTMLDivElement>("hud-hint");
  private readonly log = requireElement<HTMLUListElement>("chat-log");
  private readonly form = requireElement<HTMLFormElement>("chat-form");
  private readonly input = requireElement<HTMLInputElement>("chat-input");
  private readonly closeBtn = requireElement<HTMLButtonElement>("chat-close");
  private readonly badgeEl = requireElement<HTMLSpanElement>("chat-badge");
  private readonly profileBtn = requireElement<HTMLButtonElement>("hud-profile");
  private readonly profileAvatarEl = requireElement<HTMLSpanElement>("hud-profile-avatar");
  private readonly profileNameEl = requireElement<HTMLSpanElement>("hud-profile-name");
  private readonly zoomOutBtn = requireElement<HTMLButtonElement>("hud-zoom-out");
  private readonly zoomInBtn = requireElement<HTMLButtonElement>("hud-zoom-in");
  private readonly zoomValueEl = requireElement<HTMLSpanElement>("hud-zoom-value");
  private readonly playersBtn = requireElement<HTMLButtonElement>("hud-players");
  private readonly dmBtn = requireElement<HTMLButtonElement>("hud-dm");
  private readonly dmBadge = requireElement<HTMLSpanElement>("hud-dm-badge");
  private readonly bannerEl = requireElement<HTMLDivElement>("hud-banner");
  private bannerTimer: number | null = null;
  /** A faixa atual so sai pelo botao de fechar (anuncios), nao com o tempo. */
  private bannerSticky = false;

  /** Seletor de gestos (botao no canto + grade). */
  readonly gestures = new GestureMenu();
  /** Chat de voz: microfone, sala atual, trancar e pedidos para entrar. */
  readonly voice = new VoicePanel();

  private currentHint = "";
  /** Contador de mensagens perdidas com o chat fechado no toque (o desktop sempre mostra o log). */
  private unread = 0;
  /** O log estava no fim antes da ultima mudanca de tela (ver fitChatToViewport). */
  private logAtBottom = true;
  private static readonly UNREAD_MAX = 99;

  /** A cena diz se o Enter pode abrir o chat agora (nao com um overlay aberto). */
  canChat: () => boolean = () => true;

  constructor() {
    this.form.addEventListener("submit", (event) => {
      event.preventDefault();
      const text = this.input.value.trim();
      this.input.value = "";
      this.input.blur();
      this.closeChat();
      if (text) this.chatHandler?.(text);
    });

    this.input.addEventListener("keydown", (event) => {
      if (event.key === "Escape") {
        this.input.value = "";
        this.input.blur();
        this.closeChat();
      }
      // Impede que o Phaser (que escuta na window) receba o que esta sendo digitado.
      event.stopPropagation();
    });

    this.input.addEventListener("focus", () => this.focusHandler?.(true));
    this.input.addEventListener("blur", () => {
      this.focusHandler?.(false);
      this.closeChat();
    });
    this.closeBtn.addEventListener("click", () => {
      this.input.value = "";
      this.input.blur();
      this.closeChat();
    });

    // Enter abre o chat de qualquer lugar do HUD. Fica aqui, e nao no teclado
    // do Phaser, para valer tambem com o foco num botao: em captura e com
    // preventDefault, o botao focado nao chega a receber o Enter.
    window.addEventListener(
      "keydown",
      (event) => {
        if (event.key !== "Enter" || event.ctrlKey || event.altKey || event.metaKey || event.isComposing) return;
        if (this.root.classList.contains("hidden") || this.input.disabled || !this.canChat()) return;
        const target = event.target as HTMLElement | null;
        // dentro da janela de mensagens privadas o Enter e dela (enviar, botoes)
        if (target?.closest("input, textarea, select, [contenteditable], #dm")) return;
        event.preventDefault();
        this.gestures.close();
        this.focusChat();
      },
      true,
    );

    // No toque o chat ocupa a tela toda: acompanha o pedaco que sobra acima do
    // teclado virtual (o iOS ignora o interactive-widget e empurra a pagina).
    window.visualViewport?.addEventListener("resize", () => this.fitChatToViewport(false));
    window.visualViewport?.addEventListener("scroll", () => this.fitChatToViewport(false));
    // Encolher o log (teclado subindo) nao dispara scroll, entao o valor
    // continua o de antes da mudanca: e ele que diz se o jogador lia o fim.
    this.log.addEventListener("scroll", () => {
      this.logAtBottom = this.log.scrollTop + this.log.clientHeight >= this.log.scrollHeight - 4;
    });

    this.profileBtn.addEventListener("click", () => this.profileHandler?.());
    this.playersBtn.addEventListener("click", () => {
      this.playersBtn.blur();
      this.playersHandler?.();
    });
    this.dmBtn.addEventListener("click", () => {
      this.dmBtn.blur();
      this.dmHandler?.();
    });
    // Avisos comuns somem com um toque; os fixos (anuncios) so pelo X.
    this.bannerEl.addEventListener("click", (event) => {
      if (!this.bannerSticky || (event.target as HTMLElement).closest(".banner-close")) this.hideBanner();
    });
    // blur: com o foco no botao, Espaco/Enter (interagir, chat) o clicariam de novo
    this.zoomOutBtn.addEventListener("click", () => {
      this.zoomOutBtn.blur();
      this.zoomHandler?.(-1);
    });
    this.zoomInBtn.addEventListener("click", () => {
      this.zoomInBtn.blur();
      this.zoomHandler?.(1);
    });
  }

  private chatHandler?: (text: string) => void;
  private focusHandler?: (focused: boolean) => void;
  private profileHandler?: () => void;
  private zoomHandler?: (step: number) => void;
  private playersHandler?: () => void;
  private dmHandler?: () => void;
  private nameHandler?: (id: string) => void;

  onChat(handler: (text: string) => void): void {
    this.chatHandler = handler;
  }

  onChatFocusChange(handler: (focused: boolean) => void): void {
    this.focusHandler = handler;
  }

  onProfileClick(handler: () => void): void {
    this.profileHandler = handler;
  }

  /** Lista de quem esta no Jardim (com o dossie de cada um). */
  onPlayersClick(handler: () => void): void {
    this.playersHandler = handler;
  }

  setPlayersAvailable(available: boolean): void {
    this.playersBtn.classList.toggle("hidden", !available);
  }

  /** Envelope das mensagens privadas (some ao sair do mundo). */
  onDmClick(handler: () => void): void {
    this.dmHandler = handler;
  }

  setDmAvailable(available: boolean): void {
    this.dmBtn.classList.toggle("hidden", !available);
  }

  /** Selo de mensagens privadas nao lidas; pulsa quando o numero sobe. */
  setDmUnread(count: number): void {
    const before = Number(this.dmBtn.dataset.unread ?? 0);
    this.dmBtn.dataset.unread = String(count);
    this.dmBadge.textContent = count > 99 ? "99+" : String(count);
    this.dmBadge.classList.toggle("hidden", count <= 0);
    this.dmBtn.title = count > 0 ? `Mensagens privadas: ${count} não lida(s)` : "Mensagens privadas";
    if (count > before) {
      this.dmBtn.classList.remove("pulse");
      void this.dmBtn.offsetWidth; // reinicia a animacao
      this.dmBtn.classList.add("pulse");
    }
  }

  /**
   * Faixa em destaque no topo para anuncios e avisos importantes: no toque o
   * log do chat fica escondido, e um aviso desses nao pode passar batido. A
   * comum some sozinha depois de alguns segundos ou com um toque; a fixa
   * (sticky, os anuncios do admin) so sai quando a pessoa clica no X.
   */
  showBanner(text: string, level: "info" | "warn" | "error" = "warn", sticky = false): void {
    // um aviso comum nao tira da tela um anuncio que ainda nao foi fechado
    if (this.bannerSticky && !sticky && !this.bannerEl.classList.contains("hidden")) return;
    const message = document.createElement("span");
    message.textContent = text;
    this.bannerEl.replaceChildren(message);
    this.bannerEl.className = sticky ? `${level} sticky` : level;
    this.bannerSticky = sticky;
    if (this.bannerTimer !== null) window.clearTimeout(this.bannerTimer);
    this.bannerTimer = null;
    if (sticky) {
      const close = document.createElement("button");
      close.type = "button";
      close.className = "banner-close";
      close.setAttribute("aria-label", "Fechar anúncio");
      close.textContent = "✕";
      this.bannerEl.append(close);
    } else {
      this.bannerTimer = window.setTimeout(() => this.hideBanner(), 10000);
    }
  }

  private hideBanner(): void {
    this.bannerEl.classList.add("hidden");
    this.bannerSticky = false;
    if (this.bannerTimer !== null) window.clearTimeout(this.bannerTimer);
    this.bannerTimer = null;
  }

  /** Botoes de zoom: recebe -1 (afastar) ou +1 (aproximar). */
  onZoom(handler: (step: number) => void): void {
    this.zoomHandler = handler;
  }

  /** Mostra o zoom atual e desliga o botao que passaria do limite. */
  setZoom(zoom: number, min: number, max: number): void {
    this.zoomValueEl.textContent = `${zoom}x`;
    this.zoomOutBtn.disabled = zoom <= min;
    this.zoomInBtn.disabled = zoom >= max;
  }

  /** Botao do Meu Dossie (avatar + nome); sair do Jardim fica dentro dele. */
  setProfile(user: PublicUser): void {
    this.profileBtn.classList.remove("hidden");
    this.profileNameEl.textContent = user.displayName;
    this.profileAvatarEl.style.width = `${AVATAR_FRAME.width * PROFILE_THUMB_SCALE}px`;
    this.profileAvatarEl.style.height = `${AVATAR_FRAME.height * PROFILE_THUMB_SCALE}px`;
    this.profileAvatarEl.style.backgroundImage = avatarPartsReady()
      ? `url(${sheetDataURL(user.appearance ?? presetFor(user.skin))})`
      : `url(${ASSETS.avatar(user.skin)})`;
    // altura "auto": vale para qualquer numero de linhas do spritesheet
    this.profileAvatarEl.style.backgroundSize = `${AVATAR_FRAME.width * CATALOG.frame.cols * PROFILE_THUMB_SCALE}px auto`;
  }

  show(): void {
    this.root.classList.remove("hidden");
    // anuncio ainda nao fechado volta junto com o HUD
    if (this.bannerSticky) this.bannerEl.classList.remove("hidden");
  }

  hide(): void {
    this.root.classList.add("hidden");
    // O anuncio sai da frente (ex.: minigame), mas so some de vez pelo X.
    if (this.bannerSticky) this.bannerEl.classList.add("hidden");
    else this.hideBanner();
    this.closeChat();
    this.gestures.close();
  }

  /**
   * Abre o chat (Enter no teclado, botao "Falar" no toque). No toque o campo
   * so existe enquanto esta aberto, para nao ocupar a tela nem abrir o teclado
   * virtual sem querer.
   */
  focusChat(): void {
    if (this.input.disabled) return;
    document.body.classList.add("chat-open");
    this.input.focus();
    this.setUnread(0);
    // No toque o log estava escondido: o scroll so pega depois de aparecer.
    this.fitChatToViewport(true);
  }

  /**
   * Encaixa o chat na area visivel. So volta o log para o fim ao abrir ou se o
   * jogador ja estava no fim: quem rolou para ler o historico nao e puxado de volta.
   */
  private fitChatToViewport(toBottom: boolean): void {
    if (!document.body.classList.contains("chat-open")) return;
    const viewport = window.visualViewport;
    if (viewport) {
      const style = document.documentElement.style;
      style.setProperty("--chat-top", `${viewport.offsetTop}px`);
      style.setProperty("--chat-height", `${viewport.height}px`);
    }
    if (toBottom || this.logAtBottom) this.log.scrollTop = this.log.scrollHeight;
  }

  private closeChat(): void {
    document.body.classList.remove("chat-open");
  }

  get chatFocused(): boolean {
    return document.activeElement === this.input;
  }

  setOnline(count: number): void {
    this.onlineEl.textContent = `${count} online`;
  }

  setLatency(ms: number): void {
    this.latencyValueEl.textContent = `${Math.round(ms)} ms`;

    const level = ms <= LATENCY_GOOD_MAX ? "good" : ms <= LATENCY_WARN_MAX ? "warn" : "bad";
    this.latencyDotEl.classList.remove("good", "warn", "bad");
    this.latencyDotEl.classList.add(level);
  }

  setHint(text: string | null): void {
    if (text === this.currentHint) return;
    this.currentHint = text ?? "";

    if (!text) {
      this.hintEl.classList.add("hidden");
      this.hintEl.replaceChildren();
      return;
    }
    this.hintEl.innerHTML = text;
    this.hintEl.classList.remove("hidden");
  }

  /** Clique no nome de quem falou no chat: abre o dossie (como no WhatsApp). */
  onNameClick(handler: (id: string) => void): void {
    this.nameHandler = handler;
  }

  addChat(name: string, text: string, self: boolean, id?: string): void {
    const li = document.createElement("li");
    if (self) li.className = "self";

    const who = document.createElement(id && this.nameHandler ? "button" : "span");
    who.className = "who";
    who.textContent = `${name}: `;
    if (who instanceof HTMLButtonElement && id) {
      who.type = "button";
      who.title = `Ver o dossiê de ${name}`;
      who.addEventListener("click", () => {
        who.blur();
        this.nameHandler?.(id);
      });
    }

    li.append(who, document.createTextNode(text));
    // A propria mensagem enviada nao conta como "nao lida": o formulario ja
    // fecha o chat ao submeter, e o eco do servidor chega logo em seguida.
    this.append(li, !self);
  }

  addSystem(message: string, level: "info" | "warn" | "error" = "info"): void {
    const li = document.createElement("li");
    li.className = level === "error" ? "system error" : "system";
    li.textContent = message;
    // Avisos do sistema nao contam como "nao lida": o badge e para mensagens
    // de outros jogadores, nao para notificacoes.
    this.append(li, false);
  }

  private append(li: HTMLLIElement, countsAsUnread: boolean): void {
    this.log.append(li);
    while (this.log.children.length > 60) this.log.firstElementChild?.remove();
    this.log.scrollTop = this.log.scrollHeight;

    // No toque o log fica escondido atras do botao de balao enquanto o chat
    // esta fechado; o desktop sempre mostra o log, entao nunca acumula.
    if (
      countsAsUnread &&
      document.body.classList.contains("touch") &&
      !document.body.classList.contains("chat-open")
    ) {
      this.setUnread(this.unread + 1);
    }
  }

  private setUnread(count: number): void {
    this.unread = count;
    if (count <= 0) {
      this.badgeEl.classList.add("hidden");
      this.badgeEl.textContent = "";
      return;
    }
    this.badgeEl.textContent = count > Hud.UNREAD_MAX ? `${Hud.UNREAD_MAX}+` : String(count);
    this.badgeEl.classList.remove("hidden");
  }
}

export function requireElement<T extends HTMLElement>(id: string): T {
  const el = document.getElementById(id);
  if (!el) throw new Error(`elemento #${id} não encontrado no index.html`);
  return el as T;
}
